import { calendar_v3 } from 'googleapis';
import {
  GetCoachBookingPageQuery as ClientGetCoachBookingPageQuery,
  Lessons,
  UserCustomCourts,
  Venues,
} from 'types/generated/client';
import { FetchResponse } from 'hooks/useApi';
import { CalendarEvent, FetchEventsResponse } from 'hooks/useCalendarEvents';

type CustomCourt = Pick<UserCustomCourts, 'id' | 'title' | 'fullAddress'>;

export type Lesson = Pick<
  Lessons,
  | 'id'
  | 'description'
  | 'participantLimit'
  | 'title'
  | 'startDateTime'
  | 'endDateTime'
  | 'type'
  | 'coverImagePath'
  | 'priceUnitAmount'
  | 'currency'
> & { userCustomCourt?: CustomCourt | null } & { venues?: Venues | null } & {
  ownerProfile?: {
    __typename?: 'UserProfiles';
    id?: any | null;
    preferredName?: string | null;
    profileImageFileName?: string | null;
    profileImagePath?: string | null;
    profileImageProviderUrl?: string | null;
    fullName?: string | null;
    username?: string | null;
    stripeMerchantCurrency?: string | null;
    currencyCoachDefault?: string | null;
  } | null;
} & {
  participants?:
    | null
    | {
        userProfile?: {
          preferredName?: string | null | undefined;
          fullName?: string | null | undefined;
          profileImagePath?: string | null | undefined;
        } | null;
      }[];
};

export type LessonWithLocale = Lesson & {
  startTimestamp: number;
  endTimestamp: number;
  date: string;
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
  durationMinutes: number;
  numberInInterval: number;
  indexOfInterval: number;
};

interface CalendarEventBase {
  id: string;
  title: string;
  date: string;
  startTimestamp: number;
  endTimestamp: number;
  startHour: number;
  startMinute: number;
  durationMinutes: number;
  numberInInterval: number;
  intervalVisibleCount: number;
  indexOfInterval: number;
  isOverflow: boolean;
}

export interface BounceCalendarEvent extends CalendarEventBase {
  type: 'bounce';
  lesson: LessonWithLocale;
}

export interface ExternalCalendarEvent extends CalendarEventBase {
  type: 'external';
  event: CalendarEvent;
}

export type UnifiedCalendarEvent = BounceCalendarEvent | ExternalCalendarEvent;

export interface CombinedInterval {
  start: number;
  end: number;
  totalCount: number;
  visibleCount: number;
  overflowCount: number;
}

export interface EventsByStartDateValue {
  events: UnifiedCalendarEvent[];
  intervals: CombinedInterval[];
}

export type EventsByStartDate = { [date: string]: EventsByStartDateValue };

export interface OverflowSlot {
  date: string;
  time: number;
  events: UnifiedCalendarEvent[];
}

export interface Props {
  lessons: Lesson[];
  isOwner: boolean;
  isAnonymous?: boolean;
  isTransparentHeader?: boolean;
  showName?: boolean;
  onCancelLesson?: () => void;
  coachAvailability?: ClientGetCoachBookingPageQuery['userProfiles'][0]['coachAvailability'];
  onSlotSelect?: (date: Date | null, block: CalendarBlock | null) => void;
  onDateChange?: (date: Date) => void;
}

export interface CalendarBlock {
  time: number;
  date: string;
}

export interface CalendarWeekProps {
  index: number;
  slideContent: Date[];
  now: Date | null | undefined;
  hours: number | null | undefined;
  minutes: number | null | undefined;
  toggleBottomSheet: () => void;
  scrollHeight: number;
  setScrollHeight: (height: number) => void;
  slideRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  hasSetInitialScroll: boolean;
  setHasSetInitialScroll: (hasSetInitialScroll: boolean) => void;
  activeCalendarBlock: CalendarBlock | null;
  setActiveCalendarBlock: (block: CalendarBlock | null) => void;
  onButtonClick: (block: CalendarBlock | null) => void;
  activeLessonId: string;
  setActiveLessonId: (id: string) => void;
  eventsByStartDate: EventsByStartDate;
  isOwner: boolean;
  isAnonymous?: boolean;
  showName?: boolean;
  disabledDates?: Date[];
  coachAvailability?: ClientGetCoachBookingPageQuery['userProfiles'][0]['coachAvailability'];
  onOverflowClick: (slot: OverflowSlot) => void;
}

export interface CalendarSwiperProps extends Props {
  setSwiperRef: (swiperRef: any) => void;
  setMonth: (month: number) => void;
  setYear: (year: string) => void;
  toggleBottomSheet: () => void;
  activeCalendarBlock: CalendarBlock | null;
  setActiveCalendarBlock: (block: CalendarBlock | null) => void;
  onButtonClick: (block: CalendarBlock | null) => void;
  activeLessonId: string;
  setActiveLessonId: (id: string) => void;
  isOwner: boolean;
  showName?: boolean;
  coachAvailability?: ClientGetCoachBookingPageQuery['userProfiles'][0]['coachAvailability'];
  fetchExternalCalendarEvents: (
    startDate: Date,
    endDate: Date,
  ) => Promise<FetchResponse<FetchEventsResponse | string | undefined>>;
  externalEvents: CalendarEvent[];
  loading: boolean;
  onOverflowClick: (slot: OverflowSlot) => void;
}

export enum CalendarLinkType {
  GOOGLE = 'google',
  APPLE = 'apple',
  OUTLOOK = 'outlook',
}

export type GoogleEvent = calendar_v3.Schema$Event & { calendarName: string };
